// istanbul ignore file
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { type PropsWithChildren, useState } from 'react';

import { REDIRECT_WITH_ACCOUNT_DETAILS } from 'reservations/base/constants/redirects';
import is5xxError from 'reservations/base/helpers/is5xxError';
import { useErrorSnackbar } from 'reservations/base/hooks/useSnackbar';
import { useLoadingSpinner } from 'reservations/base/providers/LoadingSpinnerProvider';
import { useCustomRouter } from 'reservations/base/providers/RouterProvider';

// Limiting this to net new mutations right now
// TODO: Refactor other mutations to make use of this logic
const mutationKeys = [
  'CREATE_BOOKING',
  'UPDATE_BOOKING',
  'CANCEL_BOOKING',
  'CONFIRM_BOOKING',
  'ENROLL_CHECKLESS',
  'UNENROLL_CHECKLESS'
];

const QueryProvider = ({ children }: PropsWithChildren) => {
  const { handleHasLoaded } = useLoadingSpinner();
  const enqueueErrorSnackbar = useErrorSnackbar();
  const { handleNavigation } = useCustomRouter();

  const [client] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // NOTE: Disabling this because we're seeing unwanted redirection to 404 page on window refocus
            refetchOnWindowFocus: false
          }
        },
        queryCache: new QueryCache({
          onError: (error, query) => {
            if (
              query.meta?.onQueryError &&
              typeof query.meta.onQueryError === 'function'
            ) {
              query.meta.onQueryError(error);
            }
          },
          onSuccess: (data, query) => {
            if (
              query.meta?.onQuerySuccess &&
              typeof query.meta.onQuerySuccess === 'function'
            ) {
              query.meta.onQuerySuccess(data);
            }
          }
        }),
        mutationCache: new MutationCache({
          onError: (error, _variables, _context, mutation) => {
            const mutationKey =
              (mutation.options.mutationKey?.[0] as string) ?? '';

            // Error code exists on this error object, but isn't recognized on the type for some reason
            //@ts-ignore
            if (is5xxError(error.code)) {
              handleNavigation(REDIRECT_WITH_ACCOUNT_DETAILS);
              return;
            }

            if (mutationKeys.includes(mutationKey)) {
              enqueueErrorSnackbar(mutationKey, error.message);
            }
          },
          onSettled: (_data, _error, _variables, _context, mutation) => {
            const mutationKey =
              (mutation.options.mutationKey?.[0] as string) ?? '';

            if (mutationKeys.includes(mutationKey)) {
              handleHasLoaded();
            }
          }
        })
      })
  );

  return (
    <QueryClientProvider client={client}>
      {children}
      <ReactQueryDevtools />
    </QueryClientProvider>
  );
};

export default QueryProvider;
